/**
 * Month, week and year pickers for template inputs, built on the app's Select.
 *
 *   <MonthPicker id={id} value="2026-09" onChange={setMonth} min="2013-11-01" max={todayIso()} />    month + year
 *   <WeekPicker id={id} value="2026-W42" onChange={setWeek} min="2013-11-25" max={todayIso()} />     year + ISO week
 *   <YearSelect id={id} value={2025} onChange={setYear} min={2014} max={2026} optional />
 *
 * Weeks are ISO 8601 weeks (Monday to Sunday, week 1 holds the year's first Thursday), the numbering Product Hunt's
 * weekly leaderboard uses; each choice shows its dates. Choices after `max` or before `min` are disabled.
 */
import Select from "@/components/ui/Select";
import { fieldClasses } from "@/components/ui/TextField";

const selectClass = (invalid?: boolean) => `${fieldClasses(invalid)} h-9 px-3 text-base sm:text-sm`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY = 86400_000;
const SHORT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const years = (from: number, to: number) => Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => to - i);

/** Monday of ISO week 1, in UTC. */
const weekOneMonday = (y: number) => {
  const jan4 = Date.UTC(y, 0, 4);
  return jan4 - ((new Date(jan4).getUTCDay() + 6) % 7) * DAY;
};
export const weeksIn = (y: number) => Math.round((weekOneMonday(y + 1) - weekOneMonday(y)) / (7 * DAY));
const mondayOf = (y: number, w: number) => new Date(weekOneMonday(y) + (w - 1) * 7 * DAY);
const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** The ISO week a calendar day falls in. */
export function isoWeekOf(iso: string): { year: number; week: number } {
  const d = new Date(`${iso}T00:00:00Z`);
  const thursday = new Date(d.getTime() + (3 - ((d.getUTCDay() + 6) % 7)) * DAY);
  const year = thursday.getUTCFullYear();
  return { year, week: Math.round((thursday.getTime() - weekOneMonday(year)) / (7 * DAY)) + 1 };
}

/** "2026/42", "2026-W42" -> { year, week }; "" or unreadable -> null. */
export function parseWeekValue(v: string): { year: number; week: number } | null {
  const m = v.trim().match(/^(\d{4})\s*[-/ ]?\s*W?(\d{1,2})$/i);
  return m ? { year: Number(m[1]), week: Number(m[2]) } : null;
}

/** "2026/9", "2026-09" -> { year, month }. */
export function parseMonthValue(v: string): { year: number; month: number } | null {
  const m = v.trim().match(/^(\d{4})[-/.](\d{1,2})$/);
  return m ? { year: Number(m[1]), month: Number(m[2]) } : null;
}

interface PeriodProps {
  id: string;
  value: string;
  onChange: (v: string) => void;
  /** Dates (YYYY-MM-DD). */
  min?: string;
  max?: string;
  invalid?: boolean;
  label: string;
}

export function MonthPicker({ id, value, onChange, min, max, invalid, label }: PeriodProps) {
  const now = new Date();
  const cur = parseMonthValue(value) ?? { year: now.getFullYear(), month: now.getMonth() + 1 };
  const maxKey = max?.slice(0, 7);
  const minKey = min?.slice(0, 7);
  const key = (y: number, m: number) => `${y}-${String(m).padStart(2, "0")}`;
  const set = (y: number, m: number) => {
    // Keep the choice inside the bounds when the year changes.
    let k = key(y, m);
    if (maxKey && k > maxKey) k = maxKey;
    if (minKey && k < minKey) k = minKey;
    onChange(k);
  };
  return (
    <div id={id} role="group" aria-label={label} className="grid grid-cols-[1.4fr_1fr] gap-2">
      <Select
        aria-label={`${label}: month`}
        className={selectClass(invalid)}
        value={String(cur.month)}
        onChange={m => set(cur.year, Number(m))}
        options={MONTHS.map((name, i) => ({ value: String(i + 1), label: name, disabled: (!!maxKey && key(cur.year, i + 1) > maxKey) || (!!minKey && key(cur.year, i + 1) < minKey) }))}
      />
      <Select
        aria-label={`${label}: year`}
        className={selectClass(invalid)}
        value={String(cur.year)}
        onChange={y => set(Number(y), cur.month)}
        options={years(Number(minKey?.slice(0, 4) ?? 2000), Number(maxKey?.slice(0, 4) ?? now.getFullYear())).map(y => ({ value: String(y), label: String(y) }))}
      />
    </div>
  );
}

export function WeekPicker({ id, value, onChange, min, max, invalid, label }: PeriodProps) {
  const today = isoWeekOf(new Date().toISOString().slice(0, 10));
  const cur = parseWeekValue(value) ?? today;
  const maxW = max ? isoWeekOf(max) : null;
  const minW = min ? isoWeekOf(min) : null;
  const k = (w: { year: number; week: number }) => w.year * 100 + w.week;
  const set = (year: number, week: number) => {
    let w = { year, week: Math.min(week, weeksIn(year)) };
    if (maxW && k(w) > k(maxW)) w = maxW;
    if (minW && k(w) < k(minW)) w = minW;
    onChange(`${w.year}-W${String(w.week).padStart(2, "0")}`);
  };
  const range = (y: number, w: number) => {
    const mon = mondayOf(y, w);
    return `${SHORT.format(mon)} to ${SHORT.format(new Date(mon.getTime() + 6 * DAY))}`;
  };
  return (
    <div>
      <div id={id} role="group" aria-label={label} className="grid grid-cols-[1fr_2fr] gap-2">
        <Select
          aria-label={`${label}: year`}
          className={selectClass(invalid)}
          value={String(cur.year)}
          onChange={y => set(Number(y), cur.week)}
          options={years(minW?.year ?? 2013, maxW?.year ?? today.year).map(y => ({ value: String(y), label: String(y) }))}
        />
        <Select
          aria-label={`${label}: week`}
          className={selectClass(invalid)}
          value={String(cur.week)}
          onChange={w => set(cur.year, Number(w))}
          // Newest first, and only weeks that can be picked (no scrolling past future weeks).
          options={Array.from({ length: weeksIn(cur.year) }, (_, i) => i + 1)
            .reverse()
            .filter(w => !(maxW && k({ year: cur.year, week: w }) > k(maxW)) && !(minW && k({ year: cur.year, week: w }) < k(minW)))
            .map(w => ({ value: String(w), label: `Week ${w} · ${range(cur.year, w)}` }))}
        />
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {[
          { label: "This week", w: today },
          { label: "Last week", w: isoWeekOf(isoDay(new Date(Date.now() - 7 * DAY))) },
        ].map(s => (
          <button
            key={s.label}
            type="button"
            onClick={() => set(s.w.year, s.w.week)}
            aria-pressed={cur.year === s.w.year && cur.week === s.w.week}
            className="rounded-full border border-line px-2.5 py-0.5 text-xs text-fg-secondary outline-none hover:border-line-strong hover:text-fg focus-visible:ring-2 focus-visible:ring-primary aria-pressed:border-primary aria-pressed:bg-primary-subtle aria-pressed:text-primary-text"
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function YearSelect({
  id,
  value,
  onChange,
  min = 1900,
  max = new Date().getFullYear(),
  optional,
  invalid,
  label,
}: {
  id: string;
  value: number | null;
  onChange: (v: number | null) => void;
  min?: number;
  max?: number;
  optional?: boolean;
  invalid?: boolean;
  label: string;
}) {
  return (
    <div id={id}>
      <Select
        aria-label={label}
        className={selectClass(invalid)}
        value={value == null ? "" : String(value)}
        placeholder="Choose a year"
        onChange={v => onChange(v ? Number(v) : null)}
        options={[...(optional ? [{ value: "", label: "Any year" }] : []), ...years(min, max).map(y => ({ value: String(y), label: String(y) }))]}
      />
    </div>
  );
}
